export const extractManualClientName = (manualInput?: string): string | undefined => {
  if (!manualInput) return undefined;
  const lines = manualInput
    .split('\n')
    .map(line => line.trim())
    .filter(Boolean);
  return lines[0];
};

const formatSlugToWords = (value: string): string => {
  const normalized = value
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) {
    return value.trim();
  }

  return normalized
    .split(' ')
    .map(word => (word ? word[0].toUpperCase() + word.slice(1) : ''))
    .join(' ')
    .trim();
};

const deriveNameFromUrl = (url?: string): string | undefined => {
  if (!url) return undefined;

  try {
    const parsed = new URL(url.startsWith('http') ? url : `https://${url}`);
    const hostname = parsed.hostname.replace(/^www\./, '');
    const hostParts = hostname.split('.');

    if (hostParts.length > 2) {
      return formatSlugToWords(hostParts.slice(0, -1).join(' '));
    }

    if (hostParts.length === 2) {
      return formatSlugToWords(hostParts[0]);
    }

    return formatSlugToWords(hostname);
  } catch {
    // If parsing fails treat as slug or plain text
    return formatSlugToWords(url);
  }
};

export const deriveClientName = ({
  metadataName,
  extractedName,
  manualInput,
  url,
  fallback
}: {
  metadataName?: string;
  extractedName?: string;
  manualInput?: string;
  url?: string;
  fallback?: string;
}): string => {
  const candidates = [
    metadataName?.trim(),
    extractedName?.trim(),
    extractManualClientName(manualInput),
    deriveNameFromUrl(url),
    fallback?.trim()
  ];

  const resolved = candidates.find(name => !!name?.length);
  return resolved || 'Unknown Client';
};

export const formatClientNameForDisplay = (clientName: string): string => {
  if (!clientName) return 'Unknown Client';

  const trimmed = clientName.trim();
  if (!trimmed) return 'Unknown Client';

  const looksLikeUrl = trimmed.includes('://') || trimmed.includes('.') || trimmed.includes('/');
  if (looksLikeUrl) {
    const fromUrl = deriveNameFromUrl(trimmed);
    if (fromUrl) {
      return fromUrl;
    }
  }

  const containsSpaces = trimmed.includes(' ');
  const containsSlugChars = /[-_]/.test(trimmed);
  const isLowercaseWord = !containsSpaces && trimmed === trimmed.toLowerCase();

  if (containsSlugChars || isLowercaseWord) {
    return formatSlugToWords(trimmed);
  }

  return trimmed;
};
